import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const CreatePublicItineraryModal = ({ open, onClose }) => {
  const [itineraryData, setItineraryData] = useState({
    name: '',
    description: '',
    duration: 1,
    maxGroupSize: 10,
    price: 0,
  });

  const [pois, setPois] = useState([]);
  const [selectedPois, setSelectedPois] = useState([]);
  const [selectedPoiData, setSelectedPoiData] = useState({
    poiId: '',
    day: 1,
    startTime: '',
    duration: 60,
    order: 1,
  });

  useEffect(() => {
    if (open) {
      fetchPois();
    }
  }, [open]);

  const fetchPois = async () => {
    try {
      const response = await axios.get('/general/pois');
      setPois(response.data);
    } catch (error) {
      console.error('Error fetching POIs:', error);
      toast.error('Failed to load Points of Interest');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setItineraryData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePoiDataChange = (field, value) => {
    setSelectedPoiData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addPoiToItinerary = () => {
    if (!selectedPoiData.poiId) {
      toast.error('Please select a POI');
      return;
    }

    if (!selectedPoiData.startTime || selectedPoiData.startTime < 0 || selectedPoiData.startTime > 23) {
      toast.error('Please enter a valid hour (0-23)');
      return;
    }

    const poi = pois.find(p => p.id === selectedPoiData.poiId);
    if (!poi) return;

    setSelectedPois(prev => [...prev, {
      ...selectedPoiData,
      poiName: poi.name
    }]);

    setSelectedPoiData({
      poiId: '',
      day: selectedPoiData.day,
      startTime: '',
      duration: 60,
      order: selectedPois.length + 2,
    });
  };

  const removePoiFromItinerary = (index) => {
    setSelectedPois(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      if (selectedPois.length === 0) {
        toast.error('Please add at least one POI to the itinerary');
        return;
      }

      const payload = {
        ...itineraryData,
        pois: selectedPois.map(poi => ({
          poiId: poi.poiId,
          day: poi.day,
          startTime: parseInt(poi.startTime),
          duration: poi.duration,
          order: poi.order
        }))
      };

      await axios.post('/admin/public-itineraries', payload);
      toast.success('Public itinerary created successfully');
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error creating public itinerary:', error);
      toast.error('Failed to create public itinerary');
    }
  };

  const resetForm = () => {
    setItineraryData({
      name: '',
      description: '',
      duration: 1,
      maxGroupSize: 10,
      price: 0,
    });
    setSelectedPois([]);
    setSelectedPoiData({
      poiId: '',
      day: 1,
      startTime: '',
      duration: 60,
      order: 1,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Public Itinerary</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={itineraryData.name}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              multiline
              rows={3}
              value={itineraryData.description}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Duration (days)"
              name="duration"
              type="number"
              value={itineraryData.duration}
              onChange={handleInputChange}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Max Group Size"
              name="maxGroupSize"
              type="number"
              value={itineraryData.maxGroupSize}
              onChange={handleInputChange}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Price"
              name="price"
              type="number"
              value={itineraryData.price}
              onChange={handleInputChange}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
              Add Points of Interest
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Point of Interest</InputLabel>
              <Select
                value={selectedPoiData.poiId}
                onChange={(e) => handlePoiDataChange('poiId', e.target.value)}
                label="Point of Interest"
              >
                {pois.map((poi) => (
                  <MenuItem key={poi.id} value={poi.id}>
                    {poi.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Day"
              type="number"
              value={selectedPoiData.day}
              onChange={(e) => handlePoiDataChange('day', parseInt(e.target.value))}
              inputProps={{ min: 1, max: itineraryData.duration }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Start Hour (0-23)"
              type="number"
              value={selectedPoiData.startTime}
              onChange={(e) => handlePoiDataChange('startTime', e.target.value)}
              inputProps={{ min: 0, max: 23 }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Duration (min)"
              type="number"
              value={selectedPoiData.duration}
              onChange={(e) => handlePoiDataChange('duration', parseInt(e.target.value))}
              inputProps={{ min: 15, step: 15 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addPoiToItinerary}
              >
                Add POI
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <List>
              {selectedPois.map((poi, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={poi.poiName}
                    secondary={`Day ${poi.day} - ${poi.startTime}:00 (${poi.duration} min)`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => removePoiFromItinerary(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          onClose();
          resetForm();
        }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Create Itinerary
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePublicItineraryModal; 